import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const symbolBasicsCode = `// Architectural Logic: Symbol Uniqueness
// Every Symbol is guaranteed to be unique, even with identical descriptions
const sym1 = Symbol('meta');
const sym2 = Symbol('meta');

console.log(sym1 === sym2); // false

// Application: "Private" internal state keys
const _INTERNAL_ID = Symbol('id');
const userNode = {
    username: 'rohitn',
    [_INTERNAL_ID]: 'ff-992-00' // Hidden from standard iteration
};

console.log(Object.keys(userNode)); // ['username']
console.log(JSON.stringify(userNode)); // {"username":"rohitn"}`;

const wellKnownSymbolsCode = `// Engineering Pattern: Well-Known Symbols
// Customizing core language behavior

const collection = {
    items: ['Vite', 'React', 'ESLint'],
    
    // Symbol.iterator makes the object "Iterable"
    [Symbol.iterator]: function* () {
        for (const item of this.items) {
            yield item;
        }
    },
    
    // Symbol.toStringTag customizes Object.prototype.toString
    get [Symbol.toStringTag]() {
        return 'EngineeringToolkit';
    }
};

for (const tool of collection) {
    console.log(tool); // Vite, React, ESLint
}

console.log(Object.prototype.toString.call(collection)); // [object EngineeringToolkit]`;

const globalRegistryCode = `// Production Pattern: Global Symbol Registry
// Use Symbol.for() to share symbols across different scripts/realms
const SHARED_KEY = Symbol.for('filefusion.auth.token');

// In another file or module
const RECEIVED_KEY = Symbol.for('filefusion.auth.token');

console.log(SHARED_KEY === RECEIVED_KEY); // true

// Retreiving the key string
console.log(Symbol.keyFor(SHARED_KEY)); // 'filefusion.auth.token'`;

const JSSymbols = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Metaprogramming</div>
                <h1>Symbols & Well-Known Symbols</h1>
                <p className="hero-lead">
                    Master JavaScript's hidden unique identifiers. Learn how to use Symbols
                    for collisions-free property keys, internal state management, and
                    customizing core language behaviors through Well-Known Symbols.
                </p>
            </div>

            <div className="content-section">
                <h2>The Primitive of Uniqueness</h2>
                <p>
                    A `Symbol` is a primitive data type that is guaranteed to be unique.
                    Unlike strings, two symbols created with the same description are
                    completely different references. This makes them ideal for
                    "private-ish" object properties that shouldn't be touched by
                    external code or external libraries.
                </p>
                <CodeBlock code={symbolBasicsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Well-Known Symbols</h2>
                <p>
                    JavaScript has built-in "Well-Known" Symbols that act as "hooks"
                    into the language internals. By defining these symbols on your
                    objects, you can change how they behave with operators (like `+`),
                    how they appear in `toString()`, or how they iterate in `for...of` loops.
                </p>
                <CodeBlock code={wellKnownSymbolsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Global Symbol Registry</h2>
                <p>
                    Sometimes you *want* the same symbol to be shared across
                    different parts of your application (e.g., between an iframe and
                    the main window, or between different micro-frontends).
                    The `Symbol.for()` method creates or retrieves a symbol from a
                    global registry.
                </p>
                <CodeBlock code={globalRegistryCode} language="javascript" />
            </div>

            <ProTip><p>
                    While Symbols are hidden from `Object.keys()` and `JSON.stringify()`,
                    they are not strictly private. Any code can still find them using
                    `Object.getOwnPropertySymbols(obj)`. Use them for avoiding
                    **collisions**, not for **security** (sensitive data).
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Identity:** Use Symbols for properties that must never be overwritten by accident.</li>
                    <li>**Iteration:** Implement `[Symbol.iterator]` to make any object work with spread `[...]`.</li>
                    <li>**Clarity:** Use `[Symbol.toStringTag]` to give your custom classes better debug names.</li>
                    <li>**Library Dev:** Use Symbols for internal metadata to avoid polluting the public API.</li>
                    <li>**Persistence:** Remember that `Symbol.for()` is the only way to share a symbol identity.</li>
                    <li>**Discovery:** Use `Object.getOwnPropertySymbols()` when you need to audit an object for symbols.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSSymbols;



